import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { Currency } from '../types/company';
import { useTranslation } from '../hooks/useTranslation';

interface AddBankAccountModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  accountNumber: string;
  bankName: string;
  swift: string;
  iban: string;
  correspondentAccount: string;
  correspondentBank: string;
  bankAddress: string;
  currency: Currency;
  balance: string;
}

const AddBankAccountModal: React.FC<AddBankAccountModalProps> = ({ companyId, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: {
      currency: Currency.PLN,
      balance: '0',
      correspondentAccount: '',
      correspondentBank: '',
      bankAddress: ''
    }
  });

  const addBankAccountMutation = useMutation({
    mutationFn: (data: FormData) => companyService.addBankAccount(companyId, {
      name: data.name,
      accountNumber: data.accountNumber,
      bankName: data.bankName,
      swift: data.swift,
      iban: data.iban,
      correspondentAccount: data.correspondentAccount,
      correspondentBank: data.correspondentBank,
      bankAddress: data.bankAddress,
      currency: data.currency,
      balance: parseFloat(data.balance)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      reset();
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || t('companies.addAccountError');
      alert(errorMessage);
    },
    throwOnError: false // Предотвращаем проброс ошибки дальше в консоль
  });

  // Обработчик для автоматического преобразования IBAN в заглавные буквы
  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.toUpperCase();
  };

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);
    addBankAccountMutation.mutate(data, {
      onSettled: () => {
        setIsSubmitting(false);
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 my-auto max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">{t('companies.addAccount')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('companies.bankAccountName') || 'Название счета'} *
            </label>
            <input
              type="text"
              {...register('name', { required: t('validation.bankAccountNameRequired') || 'Название счета обязательно' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('companies.bankAccountNamePlaceholder') || 'Например: Основной счет'}
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('companies.accountNumber')} *
            </label>
            <input
              type="text"
              {...register('accountNumber', { required: t('validation.accountNumberRequired') })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="12345678901234567890"
            />
            {errors.accountNumber && (
              <p className="text-red-600 text-sm mt-1">{errors.accountNumber.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('companies.bankName')} *
            </label>
            <input
              type="text"
              {...register('bankName', { required: t('validation.bankNameRequired') })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="PKO Bank Polski"
            />
            {errors.bankName && (
              <p className="text-red-600 text-sm mt-1">{errors.bankName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SWIFT
              </label>
              <input
                type="text"
                {...register('swift')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="PKOPPLPW"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IBAN
              </label>
              <input
                type="text"
                {...register('iban')}
                onChange={handleIbanChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="PL61109010140000071219812874"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.correspondentAccount')}
              </label>
              <input
                type="text"
                {...register('correspondentAccount')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="10901014"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.correspondentBank')}
              </label>
              <input
                type="text"
                {...register('correspondentBank')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="National Bank of Poland"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('companies.bankAddress')}
            </label>
            <textarea
              {...register('bankAddress')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ul. Puławska 15, Warsaw, Poland 02-515"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.currency')} *
              </label>
              <select
                {...register('currency', { required: t('validation.currencyRequired') })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={Currency.PLN}>PLN - Polish Zloty</option>
                <option value={Currency.USD}>USD - US Dollar</option>
                <option value={Currency.EUR}>EUR - Euro</option>
                <option value={Currency.GBP}>GBP - British Pound</option>
                <option value={Currency.CAD}>CAD - Canadian Dollar</option>
                <option value={Currency.CNY}>CNY - Chinese Yuan</option>
              </select>
              {errors.currency && (
                <p className="text-red-600 text-sm mt-1">{errors.currency.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.balance')}
              </label>
              <input
                type="number"
                step="0.01"
                {...register('balance', { 
                  min: { value: 0, message: t('validation.balanceNonNegative') }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
              {errors.balance && (
                <p className="text-red-600 text-sm mt-1">{errors.balance.message}</p>
              )}
            </div>
          </div>

          </form>

          <div className="flex gap-3 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? t('common.adding') : t('common.add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBankAccountModal;
